import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Download,
  Eye,
  EyeOff,
  MoreHorizontal,
  Ban,
  Copy,
  FileText,
  AlertCircle,
  RefreshCw,
  Scale,
  Loader2,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { useInvoice, useAnnulInvoice, useUpdateInvoiceStatus } from '../hooks/useInvoices'
import { toast } from '../hooks/useToast'
import { generateInvoicePDF, generateInvoiceBlobUrl, InvoiceLanguage } from '../services/pdfGenerator'
import { InvoiceStatus, VAT_EXEMPTION_REASONS } from '../types/invoice'
import { getApiError } from '../lib/api'

const statusKeys: Record<InvoiceStatus, string> = {
  draft: 'invoices.status.draft',
  created: 'invoices.status.created',
  annulled: 'invoices.status.annulled',
  corrected: 'invoices.status.corrected',
  paid: 'invoices.status.paid',
  partially_paid: 'invoices.status.partiallyPaid',
}

const statusVariants: Record<InvoiceStatus, 'secondary' | 'warning' | 'success' | 'destructive' | 'default'> = {
  draft: 'secondary',
  created: 'warning',
  annulled: 'destructive',
  corrected: 'default',
  paid: 'success',
  partially_paid: 'warning',
}

const languages: { value: InvoiceLanguage; label: string; flag: string }[] = [
  { value: 'es', label: 'Espanol', flag: '🇪🇸' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'ru', label: 'Русский', flag: '🇷🇺' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'fr', label: 'Francais', flag: '🇫🇷' },
]

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const annulMutation = useAnnulInvoice()
  const updateStatusMutation = useUpdateInvoiceStatus()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLanguage, setPreviewLanguage] = useState<InvoiceLanguage>('es')
  const [autoPreviewTriggered, setAutoPreviewTriggered] = useState(false)

  const invoiceId = Number(id)
  const { data: invoice, isLoading, error, refetch } = useInvoice(invoiceId)

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const handleDownloadPdf = async (language: InvoiceLanguage) => {
    if (!invoice) return
    await generateInvoicePDF(invoice, language)
  }

  const generatePreview = useCallback(async (language: InvoiceLanguage) => {
    if (!invoice) return
    setPreviewLoading(true)
    try {
      // Revoke previous blob URL
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      const url = await generateInvoiceBlobUrl(invoice, language)
      setPreviewUrl(url)
      setShowPreview(true)
    } finally {
      setPreviewLoading(false)
    }
  }, [invoice, previewUrl])

  const handleTogglePreview = () => {
    if (showPreview) {
      setShowPreview(false)
    } else {
      generatePreview(previewLanguage)
    }
  }

  const handlePreviewLanguageChange = (language: InvoiceLanguage) => {
    setPreviewLanguage(language)
    if (showPreview) {
      generatePreview(language)
    }
  }

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  // Auto-open preview when navigating from create page with ?preview=lang
  useEffect(() => {
    const previewLang = searchParams.get('preview') as InvoiceLanguage | null
    if (previewLang && invoice && !autoPreviewTriggered) {
      setAutoPreviewTriggered(true)
      const validLangs: InvoiceLanguage[] = ['es', 'en', 'ru', 'de', 'fr']
      const lang = validLangs.includes(previewLang) ? previewLang : 'es'
      setPreviewLanguage(lang)
      generatePreview(lang)
    }
  }, [searchParams, invoice, autoPreviewTriggered, generatePreview])

  const handleDuplicate = () => {
    if (!invoice) return
    const duplicateData = {
      sender: invoice.sender,
      recipient: invoice.recipient,
      items: invoice.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        priceWithoutVat: item.priceWithoutVat,
        vatRate: item.vatRate,
        withholdingRate: item.withholdingRate,
      })),
      vatExemptionReason: invoice.vatExemptionReason,
      notes: invoice.notes,
    }
    sessionStorage.setItem('duplicateInvoice', JSON.stringify(duplicateData))
    navigate('/dashboard/invoices/create?duplicate=true')
  }

  const handleStatusChange = async (status: InvoiceStatus) => {
    if (!invoice) return
    try {
      await updateStatusMutation.mutateAsync({ id: invoice.id, status })
      toast({
        title: t('invoices.toast.statusUpdated'),
        description: t('invoices.toast.statusUpdatedDesc', { status: t(statusKeys[status]) }),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleAnnul = async () => {
    if (!invoice) return
    if (!window.confirm(t('invoices.actions.confirmAnnul'))) return

    try {
      await annulMutation.mutateAsync(invoice.id)
      toast({
        title: t('invoices.toast.annulled'),
        description: t('invoices.toast.annulledDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleCreateCorrection = () => {
    if (!invoice) return
    sessionStorage.setItem('correctionInvoice', JSON.stringify({
      originalId: invoice.id,
      sender: invoice.sender,
      recipient: invoice.recipient,
      items: invoice.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        priceWithoutVat: item.priceWithoutVat,
        vatRate: item.vatRate,
        withholdingRate: item.withholdingRate,
      })),
      vatExemptionReason: invoice.vatExemptionReason,
      notes: invoice.notes,
    }))
    navigate('/dashboard/invoices/create?correction=true')
  }

  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-64" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold">{t('invoices.detail.notFound')}</h3>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/invoices')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('invoices.detail.backToList')}
        </Button>
      </div>
    )
  }

  const exemptionReason = VAT_EXEMPTION_REASONS.find(r => r.value === invoice.vatExemptionReason)
  const hasRetention = invoice.totalWithholding > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{invoice.invoiceNumber}</h1>
              <Badge variant={statusVariants[invoice.status] as any}>
                {t(statusKeys[invoice.status])}
              </Badge>
              {invoice.correctedInvoiceId && (
                <Badge variant="secondary">
                  {t('invoices.detail.correctionOf', { id: invoice.correctedInvoiceId })}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{formatDate(invoice.date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showPreview ? 'default' : 'outline'} onClick={handleTogglePreview} disabled={previewLoading}>
            {previewLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : showPreview ? (
              <EyeOff className="mr-2 h-4 w-4" />
            ) : (
              <Eye className="mr-2 h-4 w-4" />
            )}
            {showPreview ? t('invoices.detail.hidePreview') : t('invoices.detail.previewPdf')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Download className="mr-2 h-4 w-4" />
                {t('invoices.actions.downloadPdf')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map(lang => (
                <DropdownMenuItem
                  key={lang.value}
                  onClick={() => handleDownloadPdf(lang.value)}
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDuplicate}>
                <Copy className="mr-2 h-4 w-4" />
                {t('invoices.actions.duplicate')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {invoice.status === 'draft' && (
                <DropdownMenuItem onClick={() => handleStatusChange('created')}>
                  {t('invoices.actions.markAsCreated')}
                </DropdownMenuItem>
              )}
              {invoice.status === 'created' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusChange('paid')}>
                    {t('invoices.actions.markAsPaid')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('partially_paid')}>
                    {t('invoices.actions.markAsPartiallyPaid')}
                  </DropdownMenuItem>
                </>
              )}
              {/* Annul for draft/created */}
              {(invoice.status === 'draft' || invoice.status === 'created') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={handleAnnul}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    {t('invoices.actions.annul')}
                  </DropdownMenuItem>
                </>
              )}
              {/* Create correction for paid/partially_paid */}
              {(invoice.status === 'paid' || invoice.status === 'partially_paid') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleCreateCorrection}>
                    {t('invoices.actions.createCorrection')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* PDF Preview Panel */}
      {showPreview && previewUrl && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">{t('invoices.detail.previewPdf')}</h3>
              <div className="flex items-center gap-1">
                {languages.map(lang => (
                  <Button
                    key={lang.value}
                    variant={previewLanguage === lang.value ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => handlePreviewLanguageChange(lang.value)}
                    disabled={previewLoading}
                  >
                    <span className="mr-1">{lang.flag}</span>
                    {lang.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="w-full rounded-lg border bg-muted/30 overflow-hidden" style={{ height: '80vh' }}>
              <iframe
                src={previewUrl}
                className="w-full h-full"
                title={`${t('invoices.detail.previewPdf')} - ${invoice.invoiceNumber}`}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('invoices.detail.senderInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-semibold">{invoice.sender.name}</p>
            {invoice.sender.nif && (
              <p className="text-sm text-muted-foreground">NIF: {invoice.sender.nif}</p>
            )}
            {invoice.sender.address && (
              <p className="text-sm text-muted-foreground">{invoice.sender.address}</p>
            )}
            {invoice.sender.email && (
              <p className="text-sm text-muted-foreground">{invoice.sender.email}</p>
            )}
            {invoice.sender.phone && (
              <p className="text-sm text-muted-foreground">{invoice.sender.phone}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('invoices.detail.recipientInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-semibold">{invoice.recipient.name}</p>
            {invoice.recipient.nif && (
              <p className="text-sm text-muted-foreground">NIF: {invoice.recipient.nif}</p>
            )}
            {invoice.recipient.address && (
              <p className="text-sm text-muted-foreground">{invoice.recipient.address}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('invoices.detail.invoiceInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('invoices.form.invoiceNumber')}</span>
              <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('invoices.form.invoiceDate')}</span>
              <span className="text-sm font-medium">{formatDate(invoice.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('common.status')}</span>
              <Badge variant={statusVariants[invoice.status] as any}>
                {t(statusKeys[invoice.status])}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('invoices.detail.created')}</span>
              <span className="text-sm font-medium">{formatDate(invoice.createdAt)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items + Totals */}
      <Card>
        <div className="px-6 py-4">
          <h3 className="font-semibold leading-none tracking-tight">{t('invoices.detail.itemsDetail')}</h3>
        </div>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">{t('invoices.form.description')}</TableHead>
                <TableHead className="px-4">{t('invoices.form.quantity')}</TableHead>
                <TableHead className="px-4">{t('invoices.form.priceWithoutVat')}</TableHead>
                <TableHead className="px-4">{t('invoices.form.vat')}</TableHead>
                {hasRetention && (
                  <TableHead className="px-4">{t('invoices.form.withholding')}</TableHead>
                )}
                <TableHead className="px-4 text-right">{t('invoices.form.subtotal')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.items.map(item => (
                <TableRow key={item.id}>
                  <TableCell className="px-4">{item.description || '-'}</TableCell>
                  <TableCell className="px-4">{item.quantity}</TableCell>
                  <TableCell className="px-4">{formatCurrency(item.priceWithoutVat)}</TableCell>
                  <TableCell className="px-4">{item.vatRate}%</TableCell>
                  {hasRetention && (
                    <TableCell className="px-4">{item.withholdingRate}%</TableCell>
                  )}
                  <TableCell className="px-4 text-right font-medium">{formatCurrency(item.subtotalWithVat)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Totals inside the card */}
          <div className="border-t px-6 py-4 space-y-2">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('invoices.detail.taxBase')}</span>
                  <span>{formatCurrency(invoice.baseAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('invoices.detail.vatAmount')}</span>
                  <span>{formatCurrency(invoice.totalVat)}</span>
                </div>
                {hasRetention && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('invoices.detail.withholding')}</span>
                    <span className="text-red-600">-{formatCurrency(invoice.totalWithholding)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">{t('invoices.detail.grandTotal')}</span>
                  <span className="font-bold text-lg">{formatCurrency(invoice.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment details */}
      {invoice.sender.paymentDetails && (invoice.sender.paymentDetails.iban || invoice.sender.paymentDetails.bankName) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('invoices.detail.paymentInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {invoice.sender.paymentDetails.iban && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('invoices.form.iban')}</p>
                  <p className="text-sm font-medium">{invoice.sender.paymentDetails.iban}</p>
                </div>
              )}
              {invoice.sender.paymentDetails.swift && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('invoices.form.swift')}</p>
                  <p className="text-sm font-medium">{invoice.sender.paymentDetails.swift}</p>
                </div>
              )}
              {invoice.sender.paymentDetails.bankName && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('invoices.form.bankName')}</p>
                  <p className="text-sm font-medium">{invoice.sender.paymentDetails.bankName}</p>
                </div>
              )}
              {invoice.sender.paymentDetails.accountHolder && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('invoices.form.accountHolder')}</p>
                  <p className="text-sm font-medium">{invoice.sender.paymentDetails.accountHolder}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* VAT Exemption */}
      {exemptionReason && exemptionReason.value && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2.5">
              <Scale className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-amber-900">
                  {t('invoices.detail.vatExemption')} — {exemptionReason.article}
                </p>
                <p className="text-sm text-amber-800">{t(exemptionReason.labelKey)}</p>
                {exemptionReason.descKey && (
                  <p className="text-xs text-amber-700 leading-relaxed mt-1">{t(exemptionReason.descKey)}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {invoice.notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('invoices.detail.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
